import {
  ADULT_AGE,
  CUSTOMER_CORPORATE,
  CUSTOMER_INDIVIDUAL,
  CUST_TP_CD_C,
  CUST_TP_CD_P,
  ELDERLY_AGE,
  FSS_HOST_MAP,
  PFMS_HOST_MAP,
  NATION_CODE_KOREAN,
  NO_PROFIT_VALUE,
  PROFIT_TY_CD_01,
  PROFIT_TY_CD_02,
  RSDN_FOREIGNER_LETTERS,
  ERROR_MESSAGES,
} from '@common/constants';
import { differenceInDays, parse as parseDate } from 'date-fns';
import {
  cleanServerObject,
  envType,
  isEmptyString,
  isExternal,
  setDefaultValue,
} from '.';

/**
 * 계약서비스(lips) Link URL 을 구하는 함수
 * @param {string} token 인증 토큰
 * @param {object} customerInfo 고객 정보
 * @return {string} lipsUrl
 * @author Robin
 */
export const getLipsLink = (token, customerInfo) => {
  const networkType = isExternal ? 'external' : 'internal';
  const linkEnvType = envType === 'local' ? 'dev' : envType;
  const fssHost = FSS_HOST_MAP[networkType][linkEnvType];
  const customerId = customerInfo?.custNo;

  const uri = '/SysEapp/login/TokenLogin/index.cmd';
  const url = `${fssHost}${uri}`;
  const lipsUrl = $utils.addUrlQueries(url, { token, customerId });

  return lipsUrl;
};

/**
 * PFMS Link URL 을 구하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {string} pfmsUrl
 */
export const getPfmsLink = async customerInfo => {
  const linkEnvType = envType === 'local' ? 'dev' : envType;
  const { getMainLinkToken } = $hooks.useApi();
  const token = await $utils.getLinkToken(getMainLinkToken);
  const customerId = customerInfo?.custNo;
  const pfmsHost = PFMS_HOST_MAP[linkEnvType];
  const uri = '/pfms/pfms0002r.do';
  const url = `${pfmsHost}${uri}`;
  const pfmsUrl = $utils.addUrlQueries(url, { token, customerId });

  return pfmsUrl;
};

/**
 * PFMS Simul Link URL 을 구하는 함수
 * @return {string} pfmsSimulUrl
 */
export const getPfmsSimulLink = async () => {
  const linkEnvType = envType === 'local' ? 'dev' : envType;
  const { getMainLinkToken } = $hooks.useApi();
  const token = await $utils.getLinkToken(getMainLinkToken);
  const pfmsHost = PFMS_HOST_MAP[linkEnvType];
  const uri = '/pfms/pfms0000r.do';
  const url = `${pfmsHost}${uri}`;
  const pfmsSimulUrl = $utils.addUrlQueries(url, { token });

  return pfmsSimulUrl;
};

/**
 * 해당 고객의 자택 혹은 직장 주소 입력되었는지 여부
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 자택 혹은 직장 주소 입력 되었는지 여부
 * @author Robin
 */
export const checkAddressFilled = (customerInfo = {}) => {
  const { homeAddr, wkplAddr } = customerInfo;

  return !!homeAddr || !!wkplAddr;
};

/**
 * 주소 타입(userSelectedType)의 Code를 구하는 함수
 * @param {string} addressType 주소 타입 String
 * @return {string} 주소 코드 - 01: 지번 / 02: 도로명
 * @author Robin
 */
export const getAddressTypeCode = addressType => {
  if (addressType === 'R') return '02';
  else return '01';
};

/**
 * 다음 주소 API에서 나온 data 로 고객 정보에 저장할 주소 텍스트를 만드는 함수
 * @param {object} data 다음 주소 API 검색 리턴 Data
 * @return {string} 고객 정보의 주소 텍스트
 * @author Robin
 */
export const makeAddressText = data => {
  const { jibunAddress, roadAddress, buildingName, bname, userSelectedType } =
    data;
  const isRoadAddress = userSelectedType === 'R';
  let addressText;

  // 도로명 주소일 경우
  if (isRoadAddress) {
    // PC 로직
    const fmtRoadName =
      bname && buildingName
        ? `(${bname}, ${buildingName})`
        : bname || buildingName
        ? `(${bname || buildingName})`
        : '';
    addressText = roadAddress + fmtRoadName;

    // Asis 코드 보관용
    // addr = data.roadAddress;
    // // 법정동명이 있을 경우 추가한다. (법정리는 제외)
    // // 법정동의 경우 마지막 문자가 "동/로/가"로 끝난다.
    // if (data.bname !== '' && /[동|로|가]$/g.test(data.bname)) {
    //   extraAddr += data.bname;
    // }
    // // 건물명이 있고, 공동주택일 경우 추가한다.
    // if (data.buildingName !== '' && data.apartment === 'Y') {
    //   extraAddr +=
    //     extraAddr !== '' ? ', ' + data.buildingName : data.buildingName;
    // }
    // // 표시할 참고항목이 있을 경우, 괄호까지 추가한 최종 문자열을 만든다.
    // if (extraAddr !== '') {
    //   extraAddr = ` (${extraAddr})`;
    // }
  }

  // 지번 주소일 경우
  else {
    addressText = jibunAddress;
  }
  return addressText;
};

/**
 * 가입설계동의 payload의 필요한 부분을 trim 처리 하는 함수
 * @param {object} payload 가입설계동의의 전송할 데이터 object
 * @return {object} 필요한 부분이 trim 처리 된 데이터 object
 * @author Robin
 */
export const trimCustomer = (payload = {}) => {
  // 이름 중간 띄어쓰기는 허용으로 변경
  const name = payload.name?.trim();
  const username = payload.username?.trim();
  // const name = payload.name?.replace(/ /g, '');
  // const username = payload.username?.replace(/ /g, '');

  return {
    ...payload,
    name,
    username: username ? username : name,
  };
};

/**
 * 필요한 부분이 trim 처리된 자식 데이터
 * @param {array} children 자식 데이터 array
 * @return {array} 필요한 부분이 trim 처리 된 자식 데이터 array
 * @author Robin
 */
export const trimChildren = (children = []) => {
  return children.map(child => trimCustomer(child));
};

/**
 * 해당 고객정보가 다른 고객 정보보다 최신인지 판별하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @param {object} comparingInfo 비교할 고객 정보 Object
 * @return {boolean} 고객 정보가 비교 정보보다 더 최신인지 여부
 * @author Robin
 */
export const checkLatestCustomerInfo = (customerInfo, comparingInfo) => {
  const customerInfoTime = customerInfo?.fetchedAt;
  const comparingInfoTime = comparingInfo?.fetchedAt;

  // 비교할 고객정보의 시간이 없으면 최신으로 판단
  if (!comparingInfoTime) return true;
  else if (customerInfoTime >= comparingInfoTime) return true;
  else return false;
};
/**
 * 고객이 개인 고객인지 판단하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean} 개인 고객 여부 (true/false)
 * @author Robin
 */
export const checkIndividual = (customerInfo = {}) => {
  const isIndividual = customerInfo?.custKind === CUSTOMER_INDIVIDUAL;
  return isIndividual;
};

/**
 * 고객이 법인 고객인지 판단하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean} 법인 고객 여부 (true/false)
 * @author Robin
 */
export const checkCorporate = (customerInfo = {}) => {
  const isCorporate = customerInfo?.custKind === CUSTOMER_CORPORATE;
  return isCorporate;
};

/**
 * 고객이 계약고객인지 여부를 확인하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean} 계약고객 여부 (true/false)
 * @author Robin
 */
export const checkContractCustomer = (customerInfo = {}) => {
  const isContractCustomer =
    customerInfo?.policyType && customerInfo?.policyType !== '가망';
  return isContractCustomer;
};

/**
 * 미성년자 여부 조회
 * @param {number} age 미성년자 여부 확인할 나이
 * @return {boolean} 미성년자 여부 (true: 미성년자 / false: 미성년자 아님)
 * @author Robin
 */
export const checkMinor = age => {
  return !$utils.isNil(age) && Number(age) < ADULT_AGE;
};

/**
 * 노약자 여부 조회
 * @param {number} age 노약자 여부 확인할 나이
 * @return {boolean} 노약자 여부 (true: 노약자 / false: 노약자 아님)
 * @author Robin
 */
export const checkElderly = age => {
  return !$utils.isNil(age) && Number(age) >= ELDERLY_AGE;
};

/**
 * 고객의 가입설계동의 만료/동의 필요 여부 확인 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean} 가입설계동의 만료 여부 [동의 필요인 경우 포함]
 * @author Robin
 */
export const checkCertiExpire = (customerInfo = {}) => {
  // 법인 고객일 경우, 만료개념이 없어 무조건 만료 아님으로 판단
  const isCorporate = checkCorporate(customerInfo);
  if (isCorporate) return false;

  // 가입설계 동의 만료일이 없는 경우, 만료[동의 필요]로 판단
  const agreeDeleteDate = customerInfo?.agreeDeleteDate;
  if (!agreeDeleteDate) return true;

  const certiExpireDate = parseDate(agreeDeleteDate, 'yyyyMMdd', new Date());
  const distance = differenceInDays(certiExpireDate, new Date());
  const isExpired = distance < 0;

  return isExpired;
};

/**
 * 고객의 가입설계동의 도래 여부 확인 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean} 가입설계동의 만료 도래 여부
 * @author Robin
 */
export const checkCertiExpireSoon = (customerInfo = {}) => {
  // 가입설계 동의 만료일이 없는 경우, 만료 도래로 판단하지 않음
  const agreeDeleteDate = customerInfo?.agreeDeleteDate;
  if (!agreeDeleteDate) return false;

  const expireSoonDay = 30;
  const certiExpireDate = parseDate(agreeDeleteDate, 'yyyyMMdd', new Date());
  const distance = differenceInDays(certiExpireDate, new Date());

  // 만료일이 지난 경우 제외
  const isExpired = checkCertiExpire(customerInfo);
  const isExpireSoon = !isExpired && distance <= expireSoonDay;

  return isExpireSoon;
};

/**
 * 고객의 가입설계동의가 기준일 이전이어서 재동의가 필요한지 여부
 * @param {object} customerInfo 고객 정보 Object
 * @param {string} baseDate 기준일
 * @return {boolean} 가입설계동의 기간이 이전이어서 재동의 필요한지 여부
 * @author Robin
 */
export const checkCertiOld = (customerInfo = {}, baseDate) => {
  // 가입설계동의일이 없는 경우, 재동의 필요라고 판단하지 않음
  const agreeDate = customerInfo?.agreeDate;
  if (!agreeDate) return false;

  /*
    가입설계 재동의 필요 기준일
    기준일 정보가 없는 경우, 기준일 판단 대상으로 보지 않음
    차후 기준일 판단이 불필요한 경우를 위해
  */
  if ($utils.isEmptyObject(baseDate)) return false;

  /*
    기준일이 잘못된 날짜 형식의 경우,
    에러 인지를 위해 메시지를 표시
    동의 없이 가입설계를 진행하는 것을 방지하기 위해 재동의하도록 한다.
  */
  const { startDate } = baseDate;
  if (!$utils.checkValidDateText(startDate)) {
    console.error(ERROR_MESSAGES.INVALID_DATE_FORMAT);
    return true;
  }

  const oldJudgingDate = parseDate(startDate, 'yyyyMMdd', new Date());
  const certiDate = parseDate(agreeDate, 'yyyyMMdd', new Date());
  const distance = differenceInDays(certiDate, oldJudgingDate);

  // 만료일이 지난 경우 제외
  const isExpired = checkCertiExpire(customerInfo);
  const isCertiOld = !isExpired && distance < 0;

  return isCertiOld;
};

/**
 * 고객의 가입설계동의 상태 값을 구하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @param {string} baseDate 가입설계동의 기준일
 * @return {'noCerti' | 'notExist' | 'expired' | 'expireSoon' | 'old' | ''} 고객의 가입설계동의 상태 값
 * @author Robin
 */
export const checkCertiStatus = (customerInfo, baseDate) => {
  // 법인은 체크 제외
  if (customerInfo?.custKind === 'C') {
    return '';
  }

  // 미인증, 인증실패인 경우
  if (customerInfo?.certiScsYn === 'N') {
    return 'noCerti';
  }

  // 가입설계 동의 만료일이 없는 경우
  const isNotExist = isEmptyString(customerInfo?.agreeDeleteDate);
  if (isNotExist) return 'notExist';

  // 만료일이 지난 경우
  const isExpired = checkCertiExpire(customerInfo);
  if (isExpired) return 'expired';

  // 가입설계동의 필요가 도래 경우
  const isExpireSoon = checkCertiExpireSoon(customerInfo);
  if (isExpireSoon) return 'expireSoon';

  // 가입설계동의가 기준일 이전인 경우
  const isOld = checkCertiOld(customerInfo, baseDate);
  if (isOld) return 'old';

  // 위 경우에 해당되지 않는 경우
  return '';
};

/**
 * 고객의 가입설계동의 상태에 따라 필요한 메세지를 구하는 함수
 * @param {'noCerti' | 'notExist' | 'expired' | 'expireSoon' | 'old'} status 가입설계 동의 상태
 * @param {string} name 고객 이름
 * @return {string} 가입설계동의 팝업에 보여줄 메시지
 * @author Robin
 */
export const checkCertiMessage = (status, name = '', baseDate) => {
  // 미인증, 인증실패
  if (status === 'noCerti') {
    return '청약을 위한 본인인증 필요 고객입니다. 고객등록사이트를 통해 인증을 진행해주세요.';
  }

  // 가입설계동의 만료일이 없거나 지났거나  기준 일자 이전인 경우
  if (status === 'notExist' || status === 'expired' || status === 'old') {
    const { startDate } = baseDate;
    const year = startDate.substring(0, 4);
    const month = startDate.substring(4, 6);
    const day = startDate.substring(6, 8);
    return `가입설계동의 내용 변경으로 ${year}년 ${month}월 ${day}일 이전 동의고객은 청약 및 사전심사를 위해 가입설계동의를 다시 진행해주세요.`;
  }

  // 가입설계동의 만료일가 도래한 경우
  else if (status === 'expireSoon') {
    const customerName = name ? `${name} ` : '';
    return (
      `${customerName}고객님의 가입설계동의 유효기간이 1개월 미만일 경우 청약서 발행이 불가합니다. (가입설계서 발행은 가능)\n` +
      '가입설계동의를 진행해 주세요.'
    );
  }

  // 위 조건들에 전부 해당하지 않는 경우
  else {
    return '';
  }
};

/**
 * 고객의 직업이 전문 금융인인지 판단하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean} 고객이 전문 금융인인지 여부
 * @author Robin
 */
export const checkFinancialExpert = (customerInfo = {}) => {
  const { jobCode } = customerInfo;
  const jobCodeNumber = Number(jobCode);

  const result = jobCodeNumber <= 51036 && jobCodeNumber >= 51031;
  return result;
};

/**
 * 현재 직업/운전코드가 이전(As-is)에 사용하던 옛날 코드인지 확인하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean}
 * @author Robin
 */
export const checkOldJobDrvCode = (customerInfo = {}) => {
  const cleanedCustomerInfo = cleanServerObject(customerInfo);
  const { oldJobYn = 'N', oldDrvYn = 'N' } = cleanedCustomerInfo;

  const isOldJobLabel = oldJobYn === 'Y';
  const isOldDrvLabel = oldDrvYn === 'Y';
  return isOldJobLabel || isOldDrvLabel;
};

/**
 * 직업/운전정보 유효성 여부 확인하는 함수
 * @param {object} customerInfo 고객 정보 Object
 * @return {boolean} 직업/운전정보 유효한지 여부
 * @author Robin
 */
export const checkValidJobDriver = customerInfo => {
  // 법인 고객은 직업정보 체크하지 않음
  const isCorporateCustomer = $utils.checkCorporate(customerInfo);
  if (isCorporateCustomer) return true;

  const { jobCode, jobLabel, drvCode, drvLabel } = customerInfo;
  const isEmptyJobDriver = [jobCode, jobLabel, drvCode, drvLabel].some(field =>
    $utils.isEmptyString(field),
  );
  const isOldJobDrv = $utils.checkOldJobDrvCode(customerInfo);
  const isValidJobDriver = !isEmptyJobDriver && !isOldJobDrv;
  return isValidJobDriver;
};

/**
 * 직업/운전정보 검색 팝업의 데이터를 고객정보의 데이터 형태로 변경하는 함수
 * @param {object} searchInfo 직업/운전 정보 검색 팝업의 Object
 * @return {object} 고객정보 상세 Object에 맞는 변경된 직업 정보 Object
 * @author Robin
 */
export const convertJobDriverInfo = searchInfo => {
  // 직업/운전정보 검색 팝업에서 사용하는 Data Key와 고객상세정보 Data의 Key 값 매칭
  const searchKeyInfo = {
    jobCd: 'jobCode',
    jobCdDesc: 'jobLabel',
    drvCd: 'drvCode',
    drvCdDesc: 'drvLabel',
  };

  // searchInfo 에서, 직업/운전정보만 추출하여 데이터 Key 변경
  const jobDriverInfo = Object.keys(searchKeyInfo).reduce((info, searchKey) => {
    const key = searchKeyInfo[searchKey];
    const value = searchInfo[key] || searchInfo[searchKey];
    info[key] = value;
    return info;
  }, {});

  return jobDriverInfo;
};

/**
 * 서버에서 내려온 고객 정보 중 잘못 내려오거나 기본값 세팅이 필요한 데이터를 정리하여 리턴하는 함수
 * @param {object} customerInfo 서버에서 내려온 고객 정보
 * @return {object} 고객 정보가 정리된 Object
 * @author Robin
 */
export const arrangeDefaultCustomer = (customerInfo = {}) => {
  const cleanedCustomerInfo = cleanServerObject(customerInfo);

  // 내국인인 경우, 국가 코드(natnCd) 대한민국으로 설정
  const isForeigner = checkForeigner(customerInfo);
  if (!isForeigner) {
    setDefaultValue(cleanedCustomerInfo, 'natnCd', 'KR');
    setDefaultValue(cleanedCustomerInfo, 'natnCdNm', '대한민국');
  }

  // *** 조회 시간 추가 [최신 데이터 비교용]
  cleanedCustomerInfo.fetchedAt = Date.now();

  return cleanedCustomerInfo;
};

/**
 * 서버로 보내는 고객 정보의 데이터를 보정해서 보내는 함수
 * @param {object} customerInfo 서버에서 내려온 고객 정보
 * @return {object} 고객 정보가 정리된 Object
 * @author Robin
 */
export const adjustUpdateCustomer = (customerInfo = {}) => {
  const { emailAddr, driveCode, drvCode, tgtCustYn } = customerInfo;

  const adjustedUpdateCustomer = {
    ...customerInfo,
    // 이메일에 #이 들어가있는 테스트 데이터 업데이트 안되므로 보정
    emailAddr: emailAddr?.replace(/[-#]/g, ''),
    // 운전정보가 driveCode / drvCode 에 들어있는 경우 있어 보정
    driveCode: driveCode || drvCode || '',
    // 타겟고객 여부가 'Y'/'N' 으로 되어있는 경우가 있어 '1' / '0' 으로 재변경
    tgtCustYn: tgtCustYn === 'Y' ? '1' : tgtCustYn === 'N' ? '0' : tgtCustYn,
  };

  return adjustedUpdateCustomer;
};

/**
 * 고객 정보에서 받는 적합성 정보(Asis)를 가입설계(Tobe)에서 사용하는 정보로 변환하는 함수
 * @param {object} suitability Asis 적합성 정보
 * @return {object} 변환된 Tobe 적합성 정보
 * @author Robin
 */
export const convertSuitabilityTobe = suitability => {
  return {
    channel: 'SAT',
    diagnosisDate: suitability?.testDate || '',
    investmentType: suitability?.invTypeText || '',
    investmentTypeCd: suitability?.invTypeCode || '',
    productType: suitability?.prdTypeText || '',
    productTypeCd: suitability?.prdTypeCode || '',
    publishNumber: suitability?.applicationNo || '',
    questionnaireNo: suitability?.reportSerialNo || '',
    suitDiagStatus: suitability?.suitabilityStatusText || '',
    diagnosisScore: suitability?.diagnosisScore || '',
    diagnosisPublishYn: suitability?.printYn || '',
    scanYn: suitability?.scanYn || '',
    certifyYn: suitability?.certifyYn || '',
    maturityDate: suitability?.productTypeEndDate || '',
    suitDiagWay: '', // 확인 필요
  };
};

/**
 * 고객정보의 고객이 외국인인지 여부를 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 외국인 인지 여부
 * @author Robin
 */
export const checkForeigner = customerInfo => {
  const { rsdn = '', corporation = {} } = customerInfo;
  const { nationCode } = corporation ?? {};
  const rsdnNationLetter = rsdn?.[6] || '';

  // 개인 고객: 주민번호 7번째 자리가 외국인 주민번호 [5, 6, 7, 8]
  // 법인 고객: 선택한 법인의 국가 코드가 대한민국('KR')이 아닌 경우
  const isIndividual = checkIndividual(customerInfo);
  const isCorporate = checkCorporate(customerInfo);
  const isForeigner = isIndividual
    ? RSDN_FOREIGNER_LETTERS.includes(rsdnNationLetter)
    : isCorporate
    ? nationCode && nationCode !== NATION_CODE_KOREAN
    : false;

  return isForeigner;
};

/**
 * 고객정보의 외국인 정보가 유효한 정보인지 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 외국인 정보 유효한지 여부
 * @author Robin
 */
export const checkValidForeigner = customerInfo => {
  const { natnCd, engName, corporation = {} } = customerInfo;
  const { nationCode } = corporation ?? {};
  const isCorporate = checkCorporate(customerInfo);
  const nationCodeValue = isCorporate ? nationCode : natnCd;

  // 개인 외국인인 경우 국적/영문명 필수
  // 법인 외국인인 경우 영문명 필수
  const isForeigner = checkForeigner(customerInfo);
  if (isForeigner && (isEmptyString(nationCodeValue) || isEmptyString(engName)))
    return false;
  return true;
};

/**
 * 고객정보의 주소 정보가 유효한 정보인지 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 주소 정보 유효한지 여부
 * @author Robin
 */
export const checkValidAddress = customerInfo => {
  const { postRcvTpCd } = customerInfo;

  const postPlace = getPostPlaceValue(postRcvTpCd);
  const homeAddress = getFullAddress(customerInfo, 'home');
  const workplaceAddress = getFullAddress(customerInfo, 'workplace');
  const isHomeAddressExist = !$utils.isEmptyString(homeAddress);
  const isWorkplaceAddressExist = !$utils.isEmptyString(workplaceAddress);

  const isAddressExist = isHomeAddressExist || isWorkplaceAddressExist;
  const isEmptyPostPlaceSelected =
    (postPlace === 'home' && !isHomeAddressExist) ||
    (postPlace === 'workplace' && !isWorkplaceAddressExist);

  // 주소 정보가 자택, 직장 중 하나가 존재하면서
  // 우편물 수령처로 선택 된 주소가 빈 값인 경우가 아니라면 유효한 주소로 판단
  const isValidAddress = isAddressExist && !isEmptyPostPlaceSelected;

  return isValidAddress;
};

/**
 * 고객정보의 우편물 수령처가 유효한 정보인지 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 우편물 수령처 유효한지 여부
 * @author Robin
 */
export const checkValidPostPlace = customerInfo => {
  const { postRcvTpCd } = customerInfo;

  // 법인인 경우 우편물 수령처 확인 안하며,
  // 개인인 경우 자택, 직장일 때만 유효한 것으로 판단
  const postPlace = getPostPlaceValue(postRcvTpCd);
  const isCorporate = checkCorporate(customerInfo);
  const isValidPostPlace =
    isCorporate || ['home', 'workplace'].includes(postPlace);

  return isValidPostPlace;
};

/**
 * 고객정보의 우편물 수령처의 코드로 Front 에서 사용하는 우편물 수령처의 value 값을 구하는 함수
 * @param {string} postRcvTpCd 고객 정보의 우편물 수령처 코드
 * @return {string} Front 에서 사용하는 우편물 수령처 value
 * @author Robin
 */
export const getPostPlaceValue = postRcvTpCd => {
  const postPlaceCode = String(postRcvTpCd);

  if (postPlaceCode === '1') return 'home';
  else if (postPlaceCode === '2') return 'workplace';
  else return 'etc';
};

/**
 * 법인 고객을 사업자등록번호로 구분하고 있는지 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 법인고객 사업자등록번호로 구분하고 있는지 여분
 * @author Robin
 */
export const checkCorporateByRegNo = customerInfo => {
  // custIdrTpCd - 2: 법인등록번호 / 4: 사업자등록번호
  const { custIdrTpCd } = customerInfo;
  const isCorporateByRegNo = String(custIdrTpCd) === '4';
  return isCorporateByRegNo;
};

/**
 * 고객의 식별번호(rsdn - 주민번호/사업자등록번호)의 유효성을 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 고객식별번호 Rsdn의 유효한지 여부
 * @author Robin
 */
export const checkValidRsdn = customerInfo => {
  const { rsdn, corporation = {} } = customerInfo;
  const { corpRegNo } = corporation;
  const isCorporate = checkCorporate(customerInfo);

  if (isCorporate) {
    return corpRegNo?.length === 10;
  } else {
    return rsdn?.length === 13;
  }
};

/**
 * 고객정보의 주소 정보가 유효한 정보인지 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @param {string?} type 주소 타입 (집: home / 직장: workplace)
 * @return {boolean} 주소 정보 유효한지 여부
 * @author Robin
 */
export const getFullAddress = (customerInfo, type) => {
  const {
    corporation,
    homeZipCd,
    homeAddr,
    homeAddrDtl,
    wkplZipCd,
    wkplAddr,
    wkplAddrDtl,
  } = customerInfo;

  // 법인 고객일 경우, 회사 정보 내에 있는 주소 리턴
  const isCorporate = checkCorporate(customerInfo);
  if (isCorporate) return corporation?.address;

  // 개인 고객일 경우, 집/직장 주소 정보를 조합해서 리턴
  let address = '';

  // 집 주소일 경우
  if (type === 'home') {
    // if (isOwnCustomer) {
    //   address += redience?.homeZipCode ? `(${redience?.homeZipCode})` : '';
    //   address += redience?.homeAddress ? ` ${redience?.homeAddress}` : '';
    //   address += redience?.homeAddressDetail
    //     ? ` ${redience?.homeAddressDetail}`
    //     : '';
    //   return address;
    // }

    address += homeZipCd ? `(${homeZipCd}) ` : '';
    address += homeAddr ? ` ${homeAddr} ` : '';
    address += homeAddrDtl ? `${homeAddrDtl}` : '';
  }

  // 직장 주소일 경우
  else if (type === 'workplace') {
    // if (isOwnCustomer) {
    //   address += redience?.officeZipCode
    //     ? `(${redience?.officeZipCode}) `
    //     : '';
    //   address += redience?.officeAddress ? ` ${redience?.officeAddress}` : '';
    //   address += redience?.officeAddressDetail
    //     ? ` ${redience?.officeAddressDetail}`
    //     : '';
    //   return address;
    // }

    address += wkplZipCd ? `(${wkplZipCd})` : '';
    address += wkplAddr ? ` ${wkplAddr}` : '';
    address += wkplAddrDtl ? ` ${wkplAddrDtl}` : '';
  }

  return address.trim();
};

/**
 * 고객 정보가 저장에 유효한지 확인하여 유효하지 않으면 에러 메시지를 리턴하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {string | undefined} 유효하지 않은 경우 에러 메시지
 * @author Robin
 */
export const getInvalidCustomerMessage = customerInfo => {
  // 외국인인 경우 국적, 영문명 필수
  const isValidForeignerData = checkValidForeigner(customerInfo);
  if (!isValidForeignerData) return '외국인인 경우 영문명, 국적은 필수입니다.';
};

/**
 * 고객 종류(개인/법인)의 코드 값을 구하는 함수
 * @param {string} custKind 고객 종류
 * @return {string} 고객 종류 구분 코드 (개인/법인)
 * @author Robin
 */
export const getCustTpCd = custKind => {
  if (custKind === CUSTOMER_INDIVIDUAL) return CUST_TP_CD_P;
  else if (custKind === CUSTOMER_CORPORATE) return CUST_TP_CD_C;
};

/**
 * 입력된 번호가 금지된 번호인지 확인하여, 금지된 번호 에러 메시지를 리턴하는 함수
 * @param {string} phoneNumber 입력한 핸드폰 번호
 * @param {array} bannedPhoneNumberInfos 금지된 핸드폰 번호의 대한 정보
 * @param {string} policyType 고객의 가입 정보
 * @return {string | undefined} 금지된 번호에 해당하는 에러 메시지
 * @author Robin
 */
export const getBannedPhoneNumberMessage = (
  phoneNumber,
  bannedPhoneNumberInfos = [],
  policyType,
) => {
  const isContractCustomer = policyType && policyType !== '가망';
  const bannedInfo = bannedPhoneNumberInfos.find(
    ({ numbers, isAllowContractCustomer }) =>
      // 계약된 고객이면서, 계약 고객은 입력할 수 있다면 에러 경우에서 제외
      !(isAllowContractCustomer && isContractCustomer) &&
      numbers.includes(phoneNumber),
  );

  return bannedInfo?.message;
};

/**
 * 본인 인증 API의 test 파라미터 값 결정하는 함수
 * @return {string} 본인 인증 API의 test 파라미터 값
 * @author Robin
 */
export const getCertiTest = () => {
  // 현재는 운영에서도 실제 인증 하지 않으므로, 전체 'Y'로 처리
  return 'Y';
  // return isPrd ? 'N' : 'Y';
};

/**
 * 법인 고객의 영리/비영리 여부를 선택 가능한지 확인하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 영리/비영리 선택 가능 여부
 * @author Robin
 */
export const checkProfitTypeSelectable = customerInfo => {
  const { corporation = {} } = customerInfo;
  const { corpRegNo } = corporation ?? {};

  // 사업자등록번호가 없는 법인고객은 영리, 비영리 선택가능
  const isCorporate = checkCorporate(customerInfo);
  const isEmptyCoprRegNo = isEmptyString(corpRegNo);

  return isCorporate && isEmptyCoprRegNo;
};

/**
 * 해당 법인 고객의 영리/비영리 Type Code를 구하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {string} 법인의 영리/비영리 Type Code
 * @author Robin
 */
export const getCorporateProfitTypeCode = customerInfo => {
  const { corporation = {}, corpCustTyCd } = customerInfo;
  const { corpRegNo } = corporation ?? {};
  const isEmptyCoprRegNo = isEmptyString(corpRegNo);

  if (isEmptyCoprRegNo) {
    return corpCustTyCd;
  } else {
    // PROFIT_TY_CD_01 - 영리 / PROFIT_TY_CD_02 - 비영리
    const isProfitCorporateRegNo =
      corpRegNo.substring(3, 5) !== NO_PROFIT_VALUE;
    return isProfitCorporateRegNo ? PROFIT_TY_CD_01 : PROFIT_TY_CD_02;
  }
};

/**
 * 해당 법인 고객이 영리/비영리 법인인지 여부
 * @param {object} customerInfo 고객 정보
 * @return {string} 법인의 영리/비영리 Type Code
 * @author Robin
 */
export const checkProfitCorporate = customerInfo => {
  const corporateProfitType = getCorporateProfitTypeCode(customerInfo);

  // PROFIT_TY_CD_01 - 영리
  return corporateProfitType === PROFIT_TY_CD_01;
};

/**
 * 법인 고객의 법인 정보가 유효한지 여부를 구하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {boolean} 유효한 법인 정보인지 여부
 * @author Robin
 */
export const checkValidCorporation = customerInfo => {
  const { rsdn, corporation = {}, corpCustTyCd, fondPuposTpCd } = customerInfo;
  const { corpRegNo } = corporation ?? {};
  const isProfitCorporate = checkProfitCorporate(customerInfo);

  // 법인정보 유효성 체크
  // 필수 정보 - 사업자등록번호(법인등록번호가 없는경우), 영리/비영리, 비영리설립목적(비영리인 경우)
  const isRsdnExist = !isEmptyString(rsdn);
  const isValidCorpRegNo =
    isRsdnExist || (!isEmptyString(corpRegNo) && corpRegNo.length === 10);
  const isValidProfitType = !isEmptyString(corpCustTyCd);
  const isValidNonProfitPurpose =
    isProfitCorporate || !isEmptyString(fondPuposTpCd);
  const isValidCorporation =
    isValidCorpRegNo && isValidProfitType && isValidNonProfitPurpose;

  return isValidCorporation;
};

/**
 * 법인 고객의 사업자 등록 번호를 구하는 함수
 * @param {object} customerInfo 고객 정보
 * @return {string} 법인의 사업자 등록 번호
 * @author Robin
 */
export const getCoporationBizNumber = customerInfo => {
  const { corporation = {} } = customerInfo;
  const { corpRegNo } = corporation ?? {};
  return corpRegNo;
};

/**
 * 고객정보의 국적 정보 Select 박스의 옵션들을 구하는 함수
 * @param {object[]} nationCodes 서버에서 받아온 국가 코드 정보
 * @return {object[]} Select 박스에서 사용할 옵션 Array
 * @author Robin
 */
export const makeNationOptions = nationCodes => {
  // 대한민국 최상단으로 수정
  const nationCodeOptions = nationCodes
    .map(({ cdVal, cmnCdNm }) => ({
      label: cmnCdNm + '(' + cdVal + ')',
      value: cdVal,
    }))
    .sort(a => (a.value === 'KR' ? -1 : 0));

  return nationCodeOptions;
};

/**
 * 고객 본인 인증(가입설계동의/재동의/본인인증) 시의 유효성 여부 체크
 * @param {object} authInfo 고객 본인 인증 시 입력된 데이터 정보
 * @return {boolean} 인증 유효여부
 * @author Robin
 */
export const checkValidCustomerAuth = (authInfo = {}) => {
  const {
    customerName,
    rsdn1,
    rsdn2,
    rsdnSecurityObj,
    mobileCorp,
    mobileTelNo,
    verifyType,

    relationCustomers,
  } = authInfo;

  let isDisabled = true;

  if (
    customerName !== '' &&
    rsdn1.length === 6 &&
    /* @NOTE: 보안 키패드 사용 여부 값에 따른 처리 */
    (($utils.checkSecureKeypad() && rsdnSecurityObj?.value?.length === 7) ||
      (!$utils.checkSecureKeypad() && rsdn2?.length === 7)) &&
    mobileTelNo.length > 9
  ) {
    // 전화 번호 인증
    if (verifyType === 'SMS' && mobileCorp !== '') {
      isDisabled = false;
    }

    // 신분증 인증
    if (verifyType === 'IDCARD') {
      isDisabled = !checkValidIdentification(
        authInfo,
        $utils.checkSecureKeypad(),
      );
    }

    // 자녀 검증
    if (relationCustomers?.length > 0) {
      const isChildrenValid = relationCustomers.every(obj => {
        return (
          obj.name &&
          obj.rsdn1.length === 6 &&
          /* @NOTE: 보안 키패드 사용 여부 값에 따른 처리 */
          (($utils.checkSecureKeypad() &&
            obj.rsdnSecurityObj?.value?.length === 7) ||
            (!$utils.checkSecureKeypad() && obj.rsdn2.length === 7))
        );
      });

      if (!isDisabled && !isChildrenValid) {
        isDisabled = true;
      }
    }
  }

  return isDisabled;
};

/**
 * 신분증 인증 유효성 여부 체크
 * @param {object} identificationInfo 신분증 정보가 있는 object
 * @return {boolean} 신분증 정보 유효성 여부
 * @author Robin
 */
export const checkValidIdentification = (
  identificationInfo,
  isSecureKeypad = false,
) => {
  let isValid = false;

  const {
    idCardType,
    idCardNo,
    regionCode,
    driveNumberSecurityObj,
    collateralNo,
    issueDateR,
    issueDateF,
  } = identificationInfo;

  // 운전면허증인 경우
  if (
    idCardType === 'D' &&
    regionCode &&
    (isSecureKeypad
      ? driveNumberSecurityObj?.value?.length === 10
      : idCardNo?.length === 10)
  ) {
    isValid = true;
  }

  // 주민등록증인 경우
  else if (
    idCardType === 'R' &&
    issueDateR?.length === 8 &&
    $utils.isBeforeDate(issueDateR)
  ) {
    isValid = true;
  }

  // 외국인 등록증인경우
  else if (
    idCardType === 'F' &&
    issueDateF?.length === 8 &&
    // collateralNo.length === 11 && // 외국인 일련번호 선택값으로 변경
    $utils.isBeforeDate(issueDateF)
  ) {
    isValid = true;
  }

  return isValid;
};

/**
 * 실명인증 진위여부 유효성 체크
 * @param {object} realNameInfo 실명인증 정보가 있는 object
 * @return {boolean} 실명인증 정보 유효성 여부
 * @author Robin
 */
export const checkValidRealNameInfo = realNameInfo => {
  const {
    certType,
    authority,
    issuedDate,
    licenseNumber,
    licenseCode,
    collateralNo,
    foreignAuthority,
    foreignIssuedDate,
  } = realNameInfo;

  const idCardType =
    certType === '1'
      ? 'R'
      : certType === '2'
      ? 'D'
      : certType === '3'
      ? 'F'
      : '';

  const identificationInfo = {
    idCardType,
    idCardNo: licenseNumber,
    regionCode: licenseCode,
    driveNumberSecurityObj: licenseNumber,
    collateralNo: collateralNo,
    issueDateR: issuedDate,
    issueDateF: foreignIssuedDate,
  };

  const isValidIdentifcation = checkValidIdentification(
    identificationInfo,
    // 실명인증에서는 보안키패드 사용안함
    false,
  );

  // 계약자 실명증표에서는 주민등록증/외국인등록증에서도 발급기관 확인
  const isValidAuthority =
    idCardType === 'D' ||
    (idCardType === 'R' && !isEmptyString(authority)) ||
    (idCardType === 'F' && !isEmptyString(foreignAuthority));

  return isValidIdentifcation && isValidAuthority;
};

/**
 * 고객 정보에서 국가명을 구하는 함수
 * @param {object[]} nationCodes 서버의 공통 코드에서 받아온 국가 정보 List
 * @param {object} customerInfo 해당 고객의 정보
 * @return {string} 국가명
 */
export const getNationName = (nationCodes, customerInfo) => {
  const natnCd = customerInfo?.natnCd;
  const targetNationInfo = nationCodes?.find(
    nationInfo => nationInfo.cdVal === natnCd,
  );
  const nationName = targetNationInfo?.cdContn || '';

  return nationName;
};

/**
 * 법인고객이 개인사업자인지 여부
 * @param {string} corpRegNo 사업자번호
 * @return {boolean} 개인사업자 여부
 * @author Robin
 */
export const checkPersonalCorporate = corpRegNo => {
  // 개인사업자 체크 기준: 사업자번호 4~5자리 01~80 / 90~99 해당
  const targetNumber = parseInt(corpRegNo.slice(3, 5));
  const isPersonalCorporate =
    (1 <= targetNumber && targetNumber <= 79) ||
    (90 <= targetNumber && targetNumber <= 99);

  return isPersonalCorporate;
};

/**
 * 법인고객이 비영리사업자인지 여부
 * @param {string} corpRegNo 사업자번호
 * @return {boolean} 비영리사업자 여부
 * @author Robin
 */
export const checkNonProfitCorporate = corpRegNo => {
  // 비영리사업자 체크 기준: 사업자번호 4~5자리 80, 89 해당
  const targetNumber = parseInt(corpRegNo.slice(3, 5));
  const isNonProfitCorporate = [80, 89].includes(targetNumber);

  return isNonProfitCorporate;
};
